import { apiClient, getData, getPage } from '@/api/client'
import type { components } from '@/api/schema'
import { blankToNull, lenientList } from '@/schemas/common'
import {
  postCardSchema,
  postDetailSchema,
  type PostBody,
  type PostCard,
  type PostDetail,
} from '@/schemas/posts'

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

/** The mock's row holds eight and scrolls. mm-be refuses anything over 20 with a 400. */
export const LATEST_POSTS = 8

/** mm-be's code for an id that names no published post. */
export const POST_NOT_FOUND = 'POST_NOT_FOUND'

const toCard = (wire: Wire<'PostCard'>) => ({
  id: wire.id,
  title: wire.title,
  summary: wire.summary,
  imageUrl: wire.image_url === null ? null : blankToNull(wire.image_url),
  category: wire.category,
  readTimeMinutes: wire.read_time_minutes,
  publishedAt: wire.published_at,
  isPremium: wire.is_premium,
})

const toBody = (wire: Wire<'PostDetail'>): PostBody => {
  if (wire.locked) return { kind: 'locked' }
  const html = wire.content === null ? null : blankToNull(wire.content)
  return html === null ? { kind: 'empty' } : { kind: 'html', html }
}

/** Exported so it is testable on a fixture without a request. */
export const mapPostDetail = (wire: Wire<'PostDetail'>): PostDetail =>
  postDetailSchema.parse({ ...toCard(wire), tags: wire.tags, body: toBody(wire) })

/**
 * Both routes are public. `api/client.ts`'s auth middleware still attaches the member's token when
 * there is a session, and that is what decides `locked`: the same premium post comes back open to
 * a plan that includes it and locked to everyone else.
 */
export const postsService = {
  /** The newest posts, newest first. No paging: `limit` is the whole ask. */
  latest: async (limit: number = LATEST_POSTS): Promise<PostCard[]> => {
    const page = await getPage<Wire<'PostCard'>>(() =>
      apiClient.GET('/api/v5/posts/', { params: { query: { limit } } }),
    )
    return lenientList(postCardSchema, 'post').parse(page.rows.map(toCard))
  },

  /** One post by its id. 404 `POST_NOT_FOUND` when there is no such published post. */
  post: async (postId: number): Promise<PostDetail> =>
    mapPostDetail(
      await getData<Wire<'PostDetail'>>(() =>
        apiClient.GET('/api/v5/posts/{post_id}/', { params: { path: { post_id: postId } } }),
      ),
    ),
}
